'use no memo'

import { useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ProjectViewHeader } from '../components/ProjectViewHeader'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { ProjectTabValue } from '../types'
import { useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { ViewsListTable } from '@/routes/features/views/ViewsListTable'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/projects/$projectKey/views/',
)({
  component: ProjectViewsIndexPage,
})

function ProjectViewsIndexPage() {
  const { workspaceSlug, projectKey: projectKeyParam } = Route.useParams()
  const projectKey = projectKeyParam.toUpperCase()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((s) => s.workspaceId)

  const { projects, isLoading } = useProjects()

  const project = useMemo<ProjectDocument | null>(() => {
    return (
      projects.find((entry) => {
        if (entry.isDeleted) return false
        if (entry.projectKey !== projectKey) return false
        if (workspaceId && entry.envId !== workspaceId) return false
        return true
      }) ?? null
    )
  }, [projects, projectKey, workspaceId])

  const handleTabChange = (tab: ProjectTabValue) => {
    if (tab === 'views') return
    navigate({
      to: '/$workspaceSlug/projects/$projectKey',
      params: { workspaceSlug, projectKey: projectKeyParam },
      search: tab === 'tasks' ? {} : { projectTab: tab },
    })
  }

  if (isLoading) {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!project) return null

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden">
      <div className="flex-shrink-0">
        <ProjectViewHeader
          projectId={project.id}
          activeTab="views"
          onTabChange={handleTabChange}
        />
      </div>
      <div className="flex-1 min-h-0 overflow-hidden">
        <ViewsListTable
          scope="project"
          scopeEntityId={project.id}
          scopeKey={projectKeyParam}
          workspaceSlug={workspaceSlug}
        />
      </div>
    </div>
  )
}
